import { ValidSource } from "@validator/schema";
import { scrubArchive } from "@shared/utils";

import { createUploadToken } from "@/api/uploadToken";
import { sha256Base64, uploadToS3 } from "@/api/s3";
import { simulateUploadEvent } from "@/api/dev";

import { AppState, Dispatch } from "@/reducers/app";
import { WriteSubmissionAction } from "@/reducers/submission";

import { SubmissionType } from "@/context/AppContext";
import { logUploadError } from "@/api/alerts";

function onUploadError(
  dispatch: Dispatch,
  state: AppState,
  source: ValidSource | "unknown",
  message: string,
) {
  const study = state.study.study;
  if (study) {
    logUploadError({
      studyKey: study.key,
      studyId: study.id,
      participantId: study.participantId,
      participantCode: study.participantCode,
      timestamp: new Date().toISOString(),
      source,
      errors: [message],
    });
  }

  dispatch({ type: WriteSubmissionAction.UploadError, message });
}

export async function onUpload(
  dispatch: Dispatch,
  state: AppState,
  submissionType: SubmissionType,
) {
  if (submissionType === "public") {
    return dispatch({ type: WriteSubmissionAction.Uploaded, key: "" });
  }

  const { validation, writeState } = state.submission;

  if (writeState.status === "uploading") {
    return;
  }

  if (
    !state.study.study ||
    !(
      validation?.status === "accepted" ||
      (validation?.status === "rejected" &&
        validation.failure.overridable &&
        validation.overrideValidation)
    )
  ) {
    return onUploadError(dispatch, state, "unknown", "Invalid upload state");
  }

  const accepted = validation.status === "accepted";
  const detectedSource = accepted ? validation.result.data.source : validation.failure.source;
  const source = detectedSource === "unknown" && !accepted ? validation.userSource : detectedSource;

  if (!source) {
    return onUploadError(dispatch, state, "unknown", "No source selected");
  }

  const week =
    state.submission.defaultWeek !== null && !state.submission.overrideDefaultWeek
      ? state.submission.defaultWeek
      : state.submission.week;

  if (week === null) {
    return onUploadError(dispatch, state, source, "No week selected");
  }

  dispatch({ type: WriteSubmissionAction.Uploading, progress: 0 });

  const result = validation.result;

  try {
    let file = result
      ? new File([JSON.stringify(result.data.content)], validation.file.name, {
          type: "application/json",
        })
      : validation.file;

    if (!result && validation.status === "rejected" && validation.fileType === "zip") {
      const blob = await scrubArchive(file);
      file = new File([blob], file.name, { type: "application/zip" });
    }

    const token = await createUploadToken({
      studyKey: state.study.study.key,
      filename: file.name,
      size: file.size,
      sha256: await sha256Base64(file),
      source,
      validated: result !== null,
      truncated: result !== null && validation.truncated,
      week,
      report: result ? validation.report : null,
    });

    await uploadToS3(token, file, (progress) =>
      dispatch({ type: WriteSubmissionAction.Uploading, progress }),
    );

    if (import.meta.env.DEV) {
      await simulateUploadEvent(token.key, file.size);
    }

    dispatch({ type: WriteSubmissionAction.Uploaded, key: token.key });
  } catch (e) {
    const message = e instanceof Error ? e.message : "Upload failed";
    onUploadError(dispatch, state, source, message);
  }
}
